import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { leadApi } from '../api/endpoints.js';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../auth/AuthContext.jsx';
import PageHeader from '../components/PageHeader.jsx';
import Spinner from '../components/Spinner.jsx';
import EmptyState from '../components/EmptyState.jsx';
import LeadBrief from '../components/LeadBrief.jsx';
import CommentThread from '../components/CommentThread.jsx';
import { formatDate, fullName } from '../utils/format.js';

/**
 * What the sales rep opens before the meeting.
 *
 * The brief comes first and is written in sentences: the situation, the problem,
 * what we should propose, and what to confirm. The raw answers are underneath
 * for whoever wants to check a detail.
 */
const LeadDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage;

  const { data: lead, loading, error } = useApi(() => leadApi.get(id, locale), [id, locale]);

  if (loading) return <Spinner full />;
  if (error) return <EmptyState title={t('common.error')} description={error.message} />;

  const brief = lead.brief;

  return (
    <>
      <PageHeader
        title={lead.customerName ?? t('leads.title')}
        subtitle={`${t('leads.createdBy')} ${fullName(lead.telepro)} - ${formatDate(lead.createdAt, locale)}`}
        actions={
          <div className="header-actions">
            <button type="button" className="btn" onClick={() => navigate(-1)}>
              {t('common.back')}
            </button>
            {lead.customerId ? (
              <button type="button" className="btn" onClick={() => navigate(`/customers/${lead.customerId}`)}>
                {t('customers.detail')}
              </button>
            ) : null}
            {user.role === 'TELEPRO' ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => navigate(`/telepro/qualification/${lead.id}`)}
              >
                {t('common.edit')}
              </button>
            ) : null}
          </div>
        }
      />

      <LeadBrief brief={brief} />

      {/* Same thread as the customer page - one conversation per account. */}
      {lead.customerId ? <CommentThread customerId={lead.customerId} leadId={lead.id} /> : null}

    </>
  );
};

export default LeadDetailPage;
